import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../../lib/api";
import { tx, useLang } from "../../../lib/i18n";
import { adminField, AdminLabel } from "../../../pages/admin/AdminPages";
import { loadImage, toCanvas, render } from "./ops";

export function AIPanel({ slot, commit, sendable }) { useLang();
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (prompt.trim().length < 3) { toast.error(tx("Describe how you want the image to look")); return; }
    setBusy(true);
    try {
      const composed = render(slot.current, slot.params, slot.bgImage);
      const { data } = await api.post("/admin/image-studio/ai-edit", { image: await sendable(composed), prompt, negative, keep_product: keep }, { timeout: 320000 });
      const out = toCanvas(await loadImage(data.image));
      commit({ current: out, restoreSrc: out, params: { ...slot.params, mode: "standard", crop: null, rotation: 0, straighten: 0, bg: { ...slot.params.bg, type: "transparent" } }, ai: [...new Set([...slot.ai, "ai_edit"])] });
      toast.success(tx("AI edit ready. Compare it with the original below."));
    } catch (e) { toast.error(tx(errMsg(e))); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-[var(--mv-border)] p-3" data-testid="studio-ai-panel">
      <AdminLabel>{tx("Describe How You Want Your Image to Look")}</AdminLabel>
      <textarea rows={3} className={adminField} value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1500}
        placeholder={tx("e.g. Place this product in an elegant showroom with soft turquoise lighting and studio shadows")} data-testid="studio-ai-prompt" />
      <input className={adminField} value={negative} onChange={(e) => setNegative(e.target.value)} maxLength={600} placeholder={tx("Things you do NOT want (optional)")} data-testid="studio-ai-negative" />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="studio-ai-keep" />{tx("Keep product unchanged")}</label>
      <button onClick={run} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--mv-teal)] py-2 text-xs font-bold text-[var(--mv-teal)] hover:bg-[var(--mv-teal-dim)]" data-testid="studio-ai-edit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{tx(busy ? "AI is editing…" : "AI EDIT IMAGE")}
      </button>
      <p className="text-[11px] text-[var(--mv-dim)]">{tx("Seedream v4 Edit · about $0.03 per run from your Universal Key. Runs only when you click. Use original or authorised photos only.")}</p>
    </div>
  );
}

export function GeneratePanel({ onDone, onCancel }) { useLang();
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [size, setSize] = useState("landscape_4_3");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (prompt.trim().length < 3) { toast.error(tx("Describe the scene you want")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/image-studio/generate", { prompt, negative, size }, { timeout: 320000 });
      const blob = await (await fetch(data.image)).blob();
      await onDone(new File([blob], "mivo-generated.png", { type: blob.type }));
    } catch (e) { toast.error(tx(errMsg(e))); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-[var(--mv-teal)]/40 p-4" data-testid="studio-generate-panel">
      <AdminLabel>{tx("Generate Original Marketing Image")}</AdminLabel>
      <p className="text-[11px] text-[var(--mv-dim)]">{tx("Creates a brand-new original scene from text. It is not a photo of a real product and is labelled AI-generated in your library.")}</p>
      <textarea rows={3} className={adminField} value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1500} placeholder={tx("Describe the scene, e.g. a minimal charcoal showroom with turquoise accent lights and an empty podium")} data-testid="studio-gen-prompt" />
      <input className={adminField} value={negative} onChange={(e) => setNegative(e.target.value)} maxLength={600} placeholder={tx("Things you do NOT want (optional)")} data-testid="studio-gen-negative" />
      <select className={adminField} value={size} onChange={(e) => setSize(e.target.value)} data-testid="studio-gen-size">
        {[["landscape_4_3", "Landscape 4:3"], ["landscape_16_9", "Landscape 16:9"], ["square_hd", "Square"], ["portrait_4_3", "Portrait 3:4"], ["portrait_16_9", "Portrait 9:16"]].map(([k, l]) => <option key={k} value={k}>{tx(l)}</option>)}
      </select>
      <div className="flex gap-2">
        <button onClick={run} disabled={busy} className="mv-btn-primary flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold" data-testid="studio-gen-run">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{tx(busy ? "Generating…" : "GENERATE IMAGE")}
        </button>
        <button onClick={onCancel} className="rounded-lg border border-[var(--mv-border-2)] px-4 text-xs" data-testid="studio-gen-cancel">{tx("Cancel")}</button>
      </div>
      <p className="text-[11px] text-[var(--mv-dim)]">{tx("FLUX schnell · about $0.003 per image from your Universal Key.")}</p>
    </div>
  );
}
